import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAssetsList } from "../../hooks";
import { AssetSearchFilters } from "./assetFilter";

interface AssetSearchFiltersType {
  assetType: string;
  facility: string;
  system: string;
  status: string;
  page: number;
}

const getAssetTypeBadgeConfig = (type: string) => {
  switch (type?.toLowerCase()) {
    case "pipe":
      return {
        variant: "default" as const,
        className: "bg-blue-100 text-blue-800",
      };
    case "hydrant":
      return {
        variant: "secondary" as const,
        className: "bg-red-100 text-red-800",
      };
    case "valve":
      return {
        variant: "outline" as const,
        className: "bg-green-100 text-green-800",
      };
    case "meter":
      return {
        variant: "default" as const,
        className: "bg-purple-100 text-purple-800",
      };
    default:
      return {
        variant: "outline" as const,
        className: "bg-gray-100 text-gray-800",
      };
  }
};

interface AssetSearchTableProps {
  onAssetSelect?: (asset: any) => void;
  searchLocation?: {
    latitude?: number;
    longitude?: number;
    address?: string;
  };
  showSelection?: boolean;
  linkedAsset: any;
  setLinkedAsset: any;
}

export const AssetSearchTable = ({
  onAssetSelect,
  searchLocation,
  linkedAsset,
  setLinkedAsset,
}: AssetSearchTableProps) => {
  const PAGE_SIZE = 10;
  const remoteUtilityId = getRemoteUtilityId();
  const location = useLocation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");

  // Parse URL parameters for filters and pagination
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      assetType: searchParams.get("assetType") || "all",
      facility: searchParams.get("facility") || "all",
      system: searchParams.get("system") || "all",
      status: searchParams.get("status") || "all",
      page: parseInt(searchParams.get("page") || "1", 10),
      search: searchParams.get("search") || "",
    };
  }, [location.search]);

  const [currentPage, setCurrentPage] = useState(urlParams.page);
  const [filters, setFilters] = useState<AssetSearchFiltersType>({
    assetType: urlParams.assetType,
    facility: urlParams.facility,
    system: urlParams.system,
    status: urlParams.status,
    page: urlParams.page,
  });

  // Update state when URL changes
  useEffect(() => {
    setCurrentPage(urlParams.page);
    setFilters({
      assetType: urlParams.assetType,
      facility: urlParams.facility,
      system: urlParams.system,
      status: urlParams.status,
      page: urlParams.page,
    });
    setSearchTerm(urlParams.search);
  }, [urlParams]);

  // Format filters for the API call
  const formatFiltersForAPI = () => {
    const apiParams: any = {
      remote_utility_id: remoteUtilityId,
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Add search term
    if (searchTerm.trim()) {
      apiParams.search = searchTerm;
    }

    // Add filters (only if not 'all' or empty)
    if (filters.assetType && filters.assetType !== "all") {
      apiParams.assetType = filters.assetType;
    }
    if (filters.facility && filters.facility !== "all") {
      apiParams.facility = filters.facility;
    }
    if (filters.system && filters.system !== "all") {
      apiParams.system = filters.system;
    }
    if (filters.status && filters.status !== "all") {
      apiParams.status = filters.status;
    }

    return apiParams;
  };

  // Use the formatted parameters for the API call
  const { data: assetData, isLoading } = useAssetsList(formatFiltersForAPI());

  // Update URL with new parameters
  const updateUrl = (
    updates: Partial<{
      page: number;
      search: string;
      assetType?: string;
      facility?: string;
      system?: string;
      status?: string;
    }>
  ) => {
    const searchParams = new URLSearchParams(location.search);

    // Update parameters
    Object.entries(updates).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "all"
      ) {
        searchParams.set(key, String(value));
      } else {
        searchParams.delete(key);
      }
    });

    // navigate(`${location.pathname}?${searchParams.toString()}`, {
    //   replace: true,
    // });
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    updateUrl({ search: searchValue, page: 1 });
    setCurrentPage(1);
  };

  const handleFiltersChange = (newFilters: AssetSearchFiltersType) => {
    setFilters(newFilters);
    updateUrl({
      assetType: newFilters.assetType,
      facility: newFilters.facility,
      system: newFilters.system,
      status: newFilters.status,
      page: 1,
    });
    setCurrentPage(1);
  };

  // ... (keep all imports and other code the same)

  const clearFilters = () => {
    const defaultFilters = {
      assetType: "all",
      facility: "all",
      system: "all",
      status: "all",
      page: 1,
    };

    setFilters(defaultFilters);
    setSearchTerm("");
    setCurrentPage(1);

    // Clear all filter-related URL parameters
    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    updateUrl({ page: newPage });
  };

  const handleAssetSelectAction = (row: any) => {
    setLinkedAsset(row);
    if (onAssetSelect) onAssetSelect(row);
  };

  const handleClearSelection = () => {
    setLinkedAsset(null);
    if (onAssetSelect) onAssetSelect(null);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "assetNo",
      header: "Asset ID",
      type: "text",
    },
    {
      key: "assetName",
      header: "Asset Name",
      type: "text",
      render: (value: string) => value || "N/A",
    },
    {
      key: "assetTypeDisplay",
      header: "Type",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => getAssetTypeBadgeConfig(value).variant,
        getClassName: (value: string) =>
          getAssetTypeBadgeConfig(value).className,
      },
    },
    {
      key: "facilityName",
      header: "Facility",
      type: "text",
      render: (value: string) => value || "N/A",
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "text",
      render: (value: string) => (
        <div
          className={`text-sm font-medium capitalize ${
            value === "In Service"
              ? "text-green-600"
              : value === "Out of Service"
              ? "text-red-600"
              : "text-gray-600"
          }`}
        >
          {value}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      type: "custom",
      render: (_, row) => (
        <button
          onClick={() => handleAssetSelectAction(row)}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Select
        </button>
      ),
    },
  ];

  const assets = assetData?.results || [];
  const totalItems = assetData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      {searchLocation?.latitude || searchLocation?.address ? (
        <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
          <strong>Search Location:</strong>{" "}
          {searchLocation?.address ||
            `${searchLocation?.latitude?.toFixed(
              6
            )}, ${searchLocation?.longitude?.toFixed(6)}`}
        </div>
      ) : null}

      {linkedAsset ? (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-md flex justify-between items-center">
          <div className="text-sm">
            <strong>Linked Asset:</strong>{" "}
            {linkedAsset.assetNo || linkedAsset.id} -{" "}
            {linkedAsset.assetName || linkedAsset.name || "N/A"} -{" "}
            {linkedAsset.facilityName || linkedAsset.facility || "N/A"}
          </div>
          <button
            onClick={handleClearSelection}
            className="text-gray-500 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <GenericTable
          data={assets}
          columns={columns}
          rowKey="id"
          searchPlaceholder="Search by Asset ID or Name..."
          searchParamName="query"
          searchDebounceMs={300}
          searchValue={searchTerm}
          onSearch={handleSearch}
          actionsType="buttons"
          bulkActions={[]}
          emptyMessage="No assets found"
          filterPanel={
            <AssetSearchFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleFiltersChange}
              onClearFilters={clearFilters}
              loading={isLoading}
            />
          }
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
